import { useCallback, useMemo, useState } from 'react';
import { getByPath } from '@de/utils/interpolation/getByPath';

/**
 * One author-facing sort choice surfaced as a `<select>` option in the
 * repeater's toolbar. Object shape (vs. the SAD's literal `string[]`)
 * is a deliberate deviation — see DataRepeater README §Open questions
 * and {@link OpenQuestion.Q1} in `output/EDUN-10693/plan.md`. The
 * object form lets authors decouple the user-facing label from the
 * technical field path and direction without an additional parsing
 * pass at runtime.
 */
export interface SortOption {
  readonly label: string;
  /** Dot path resolved against each item, e.g. `'meta.rank'`. */
  readonly field: string;
  readonly direction: 'asc' | 'desc';
}

export interface SortState<T> {
  readonly sortedItems: ReadonlyArray<T>;
  readonly activeSortIndex: number;
  readonly canSort: boolean;
  readonly setActiveSortIndex: (index: number) => void;
}

const clamp = (value: number, lo: number, hi: number): number =>
  Math.min(hi, Math.max(lo, value));

/**
 * Order two defined values. Falls back to JS coercive comparison so
 * authors don't have to declare the field's primitive type — `'10'`
 * vs `'2'` follow string order; `10` vs `2` follow numeric order.
 *
 * Missing-value handling lives in the comparator below so it can sink
 * `undefined` predictably regardless of direction without being
 * inverted by the desc multiplier.
 */
function compareValues(a: unknown, b: unknown): number {
  if (typeof a === 'number' && typeof b === 'number') {
    return a < b ? -1 : a > b ? 1 : 0;
  }
  const sa = String(a);
  const sb = String(b);
  return sa < sb ? -1 : sa > sb ? 1 : 0;
}

function readSortKey(item: unknown, field: string): unknown {
  if (field.length === 0) return undefined;
  const segments = field
    .split('.')
    .map((name) => ({ type: 'key' as const, name }));
  return getByPath(item, segments);
}

/**
 * Stable client-side sort for a repeater's iteration array.
 *
 * Disabled when no `sortOptions` are supplied — returns the input
 * reference unchanged and exposes a no-op `setActiveSortIndex` so the
 * consumer can render its toolbar conditionally without diverging
 * branches.
 *
 * Stability — `Array.prototype.sort` is spec-stable (ES2019+), so the
 * hook does no extra index-tagging. `useMemo` ensures the sorted
 * reference flips only when `items`, `options`, or `activeSortIndex`
 * change, so `usePagination`'s reset effect fires exactly once per
 * sort flip.
 */
export function useSort<T>(
  items: ReadonlyArray<T>,
  options: ReadonlyArray<SortOption>,
): SortState<T> {
  const canSort = options.length > 0;
  const [active, setActive] = useState(0);

  const setActiveSortIndex = useCallback(
    (index: number) => {
      if (!canSort) return;
      setActive(clamp(index, 0, options.length - 1));
    },
    [canSort, options.length],
  );

  const sortedItems = useMemo(() => {
    if (!canSort || active < 0 || active >= options.length) return items;
    const opt = options[active];
    const direction = opt.direction === 'desc' ? -1 : 1;
    const copy = items.slice();
    copy.sort((a, b) => {
      const av = readSortKey(a, opt.field);
      const bv = readSortKey(b, opt.field);
      // Sink missing values to the bottom regardless of direction so
      // the desc multiplier below can't bubble them to the top.
      if (av === undefined && bv === undefined) return 0;
      if (av === undefined) return 1;
      if (bv === undefined) return -1;
      return compareValues(av, bv) * direction;
    });
    return copy;
  }, [items, options, canSort, active]);

  return useMemo<SortState<T>>(
    () => ({
      sortedItems,
      activeSortIndex: canSort ? clamp(active, 0, options.length - 1) : 0,
      canSort,
      setActiveSortIndex,
    }),
    [sortedItems, canSort, active, options.length, setActiveSortIndex],
  );
}
